import { useState } from 'react';
import { Modal, Box, Button, TextField } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import '../translations.js';


// The standard fields a contact form needs (Robert, 18.10.2026): shown by their display name, added with one
// click. `name` is the internal key the lead stores the value under.
const EXAMPLES = [
  { name: 'first_name', key: 'first_name' },
  { name: 'last_name', key: 'last_name' },
  { name: 'email', key: 'email' },
  { name: 'phone_number', key: 'phone_number' },
  { name: 'company_name', key: 'company_name' },
];

const AddParameterModal = ({ open, onClose, onSubmit, existingNames = [] }) => {
  const [formData, setFormData] = useState({ name: '', description: '', user_display: '' });
  const [showExamples, setShowExamples] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const MAX_NAME_CHARS = 50;
  const MAX_DESC_CHARS = 100;
  const MAX_DISPLAY_CHARS = 30;

  const nameCharsUsed = formData.name.length;
  const descCharsUsed = formData.description.length;
  const displayCharsUsed = formData.user_display.length;
  const nameAtLimit = nameCharsUsed === MAX_NAME_CHARS;
  const descAtLimit = descCharsUsed === MAX_DESC_CHARS;
  const displayAtLimit = displayCharsUsed === MAX_DISPLAY_CHARS;

  const handleInputChange = (field, value) => {
    if (field === 'name') {
      // Only allow letters (a-z, A-Z) and underscores, convert spaces to underscores, remove everything else
      value = value
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z_]/g, '')
        .replace(/_+/g, '_'); // collapse multiple underscores to one
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
    setFormData({ name: '', description: '', user_display: '' });
    setShowExamples(false);
    onClose();
  };

  const handleClose = () => {
    setShowExamples(false);
    onClose();
  };

  // an example is added right away – no second confirmation
  const handleUseExample = (name, description, user_display = '') => {
    onSubmit({ name, description, user_display });
    setFormData({ name: '', description: '', user_display: '' });
    setShowExamples(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2.5 : 4,
          width: isMobile ? '80%' : '90%',
          maxWidth: 500,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>{t('add_parameter')}</h3>

        <form>
          <TextField
            fullWidth
            label={t('parameter_name')}
            variant="outlined"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            margin="dense"
            required
            placeholder={t('parameter_name_placeholder')}
            inputProps={{ maxLength: MAX_NAME_CHARS }}
            helperText={`${nameCharsUsed}/${MAX_NAME_CHARS}`}
            FormHelperTextProps={{
              sx: {
                textAlign: 'right',
                mr: 0,
                color: nameAtLimit ? 'error.main' : 'text.secondary',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderColor: '#515151',
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': { color: '#5616ea' },
              },
            }}
          />

          <TextField
            fullWidth
            label={t('parameter_description')}
            variant="outlined"
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            margin="dense"
            required
            multiline
            minRows={2}
            placeholder={t('parameter_description_placeholder')}
            inputProps={{ maxLength: MAX_DESC_CHARS }}
            helperText={`${descCharsUsed}/${MAX_DESC_CHARS}`}
            FormHelperTextProps={{
              sx: {
                textAlign: 'right',
                mr: 0,
                color: descAtLimit ? 'error.main' : 'text.secondary',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderColor: '#515151',
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': { color: '#5616ea' },
              },
            }}
          />

          <TextField
            fullWidth
            label={t('parameter_user_display')}
            variant="outlined"
            value={formData.user_display}
            onChange={e => handleInputChange('user_display', e.target.value)}
            margin="dense"
            placeholder={t('parameter_user_display_placeholder')}
            inputProps={{ maxLength: MAX_DISPLAY_CHARS }}
            helperText={t('char_count_optional', { used: displayCharsUsed, max: MAX_DISPLAY_CHARS })}
            FormHelperTextProps={{
              sx: {
                textAlign: 'right',
                mr: 0,
                color: displayAtLimit ? 'error.main' : 'text.secondary',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderColor: '#515151',
              },
              '& .MuiInputLabel-root': {
                color: 'gray',
                '&.Mui-focused': { color: '#5616ea' },
              },
            }}
          />
        </form>

        <div style={{ marginTop: '15px' }}>
          <button
            type="button"
            onClick={() => setShowExamples(!showExamples)}
            style={{
              background: 'none',
              border: 'none',
              color: '#5616ea',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0,
              textDecoration: 'underline',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <FontAwesomeIcon 
              icon={showExamples ? faChevronDown : faChevronRight} 
              style={{ fontSize: '12px' }}
            />
            {t('show_some_examples')}
          </button>

          <div
            style={{
              overflow: 'hidden',
              maxHeight: showExamples ? '500px' : '0',
              transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
              opacity: showExamples ? 1 : 0,
            }}
          >
            <div
              style={{
                marginTop: '10px',
                padding: '12px',
                backgroundColor: '#f8f8f8',
                borderRadius: '8px',
                borderLeft: '3px solid #5616ea',
              }}
            >
              {EXAMPLES.map(({ name, key }, i) => {
                const display = t(`param_example_${key}_display`);
                const description = t(`param_example_${key}_desc`);
                const added = existingNames.includes(name);
                return (
                  <div key={name} style={{ marginBottom: i === EXAMPLES.length - 1 ? 0 : '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: added ? '#999' : '#5616ea' }}>{display}</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#555' }}>{description}</p>
                    </div>
                    <button
                      type="button"
                      disabled={added}
                      onClick={() => handleUseExample(name, description, display)}
                      style={{
                        marginLeft: '10px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        color: added ? '#999' : '#5616ea',
                        backgroundColor: 'white',
                        border: `1px solid ${added ? '#ccc' : '#5616ea'}`,
                        borderRadius: '4px',
                        cursor: added ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseOver={(e) => { if (!added) e.currentTarget.style.backgroundColor = '#f3eaff'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                    >
                      {added ? t('param_example_added') : t('use_example_button')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: '#5616ea',
              borderColor: '#5616ea',
              '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
            }}
          >
            {t('cancel')}
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={nameCharsUsed === 0 || descCharsUsed === 0}
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': { backgroundColor: '#4a14d6' },
            }}
          >
            {t('add')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddParameterModal;
